/**
 * Guest console (tiers T8, Beta-gated) — the REAL console experience for
 * token-holding guests, per the approved mock `mockups/homepage-guest/
 * chat.html`: "chat | inspector, exactly like the real page minus
 * sessions." Reuses ConsoleTopbar, ChatPanel, InspectorPanel, the memory
 * graph, and the full useAgentChat machinery — the only differences are
 * the guest tier banner, the unlock CTAs (Files / Memory-All / locked
 * tool rows), and the guest streaming endpoint. No Cognito anywhere: the
 * 24h guest token from `/guest/start` is the identity.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AGENTS } from '../../agents/registry.ts';
import {
  clearGuestSession,
  loadGuestSession,
  newGuestChatSessionId,
  takePendingPrompt,
} from '../../agents/guestApi.ts';
import type { StreamedMemoryRecord } from '../../agents/agentStream.ts';
import { toolsForAgent } from '../../agents/toolCatalog.ts';
import { guestConfig } from '../../config/agent-config.ts';
import { GUEST_CHAT_ENABLED } from '../../config/features.ts';
import { shouldShowAds } from '../../agents/ads.ts';
import AdSlot from '../../components/AdSlot.tsx';
import ConsoleTopbar from '../../components/console/ConsoleTopbar.tsx';
import ChatPanel from '../../components/console/ChatPanel.tsx';
import InspectorPanel from '../../components/console/InspectorPanel.tsx';
import MobileNav, { type ConsoleView } from '../../components/console/MobileNav.tsx';
import { useAgentChat } from '../../components/console/useAgentChat.ts';
import { useMemoryGraph } from '../../components/console/useMemoryGraph.ts';
import { useSessionLog } from '../../components/console/useSessionLog.ts';
import { useBannerReveal } from '../../components/console/useBannerReveal.ts';
import { logToGraph } from '../../components/console/sessionLogProjections.ts';
import '../agent/AgentConsole.css';
import './GuestConsole.css';

/** Compact OM formatting for the budget meter: 12400 → "12.4k". */
function fmtOm(n: number): string {
  if (n >= 1_000_000) {
    return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  }
  if (n >= 1_000) {
    return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}k`;
  }
  return String(n);
}

export default function GuestConsole() {
  const navigate = useNavigate();
  // One guest chat session per console visit (proxy-contract id).
  const [guestSessionId] = useState(newGuestChatSessionId);
  const [session] = useState(loadGuestSession);

  useEffect(() => {
    if (!GUEST_CHAT_ENABLED || session === null) {
      navigate('/', { replace: true });
    }
  }, [navigate, session]);

  if (!GUEST_CHAT_ENABLED || session === null) {
    return null;
  }
  return <Console token={session.token} sessionId={guestSessionId} />;
}

function Console({ token, sessionId }: { token: string; sessionId: string }) {
  // "New" re-keys the chat to a fresh guest session in place (the hook and
  // the session log both reset on sessionId) — it must NOT leave the guest
  // flow (user report: it went to sign-in).
  const [chatSessionId, setChatSessionId] = useState(sessionId);
  const agent = AGENTS.uno;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const { api, counts } = useMemoryGraph(canvasRef, popRef);
  const [view, setView] = useState<ConsoleView>('chat');
  // Same scroll-away topbar as the real console — the GUEST banner takes
  // the top of the screen once the user scrolls into the conversation.
  const bannerHidden = useBannerReveal(view);
  const [detailsOpen, setDetailsOpen] = useState(false);
  // Latest daily-budget snapshot from the runtime (rides each turn's
  // terminal frame); drives the details meter.
  const [budget, setBudget] = useState<{
    limit: number;
    used: number;
    remaining: number;
    resetAt: string;
  } | null>(null);
  // Session-memory records accumulated CLIENT-side from the stream's
  // memory-write frames — guests have no server memory API, but the "All"
  // view must still show this session's records above the unlock CTA
  // (approved mock; user report: it stayed empty).
  const [sessionRecords, setSessionRecords] = useState<readonly StreamedMemoryRecord[]>([]);

  // Session-scoped log → memory graph, exactly like the real console. No
  // transcript restore: guest conversations are this-tab-only by design.
  const log = useSessionLog(chatSessionId, null, 0);
  useEffect(() => {
    logToGraph(api, log.events);
  }, [api, log.events]);

  const navigateExpired = useNavigate();
  // A guest token expiring mid-chat can't be fixed by "signing in again" —
  // clear the session and land back on the dock for a fresh mint.
  const expireGuestSession = () => {
    clearGuestSession();
    navigateExpired('/', { replace: true });
  };
  const { items, toolLog, status, sendMessage, retryMessage } = useAgentChat(agent, log, expireGuestSession, {
    sessionId: chatSessionId,
    endpoint: { invokeUrl: guestConfig.invokeUrl, jwt: token },
    onUsageLimit: setBudget,
    onMemoryWrite: (event) => {
      if (event.moduleId === 'session' && event.op === 'append') {
        setSessionRecords((prev) => [...prev, ...(event.records ?? [])]);
      } else if (event.op === 'reset') {
        setSessionRecords([]);
      }
    },
  });

  // Turn-1 handoff from the homepage dock (take-once).
  const startedRef = useRef(false);
  useEffect(() => {
    if (!startedRef.current) {
      startedRef.current = true;
      const pending = takePendingPrompt();
      if (pending !== null && pending.trim().length > 0) {
        sendMessage(pending);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot on mount
  }, []);

  const navigate = useNavigate();
  const signUp = () => {
    clearGuestSession();
    navigate('/auth/signup');
  };

  return (
    <div
      className={
        bannerHidden
          ? 'om-console om-console--guest topbar-hidden'
          : 'om-console om-console--guest'
      }
      data-mview={view}
    >
      <ConsoleTopbar />
      {/* Guest tier banner (approved mock: gold strip, GUEST tag, details
          expander, cyan CREATE FREE ACCOUNT). */}
      <div className="om-guest-banner-wrap">
        <div className="om-guest-tier-banner">
          <b>GUEST</b>
          <span className="om-guest-tier-banner__text">
            You&rsquo;re trying Uno for free
            <span className="om-guest-tier-banner__limits">
              {' '}&mdash; small daily budget, limited tools, no memory, no files, no email/text
              messaging, no workflow automation.
            </span>
            <span className="om-guest-tier-banner__more" aria-hidden="true">
              .
            </span>{' '}
            <button
              type="button"
              className="om-guest-tier-banner__exp"
              aria-expanded={detailsOpen}
              onClick={() => setDetailsOpen((v) => !v)}
            >
              {detailsOpen ? '▾ details' : '▸ details'}
            </button>
          </span>
          <button type="button" className="om-guest-tier-banner__cta" onClick={signUp}>
            CREATE FREE ACCOUNT
          </button>
        </div>
        {detailsOpen ? (
          <div className="om-guest-tier-details">
            <p className="om-guest-tier-details__limits">
              Guest access has a small daily budget, limited tools, no memory, no files, no
              email/text messaging, and no workflow automation.
            </p>
            {budget !== null ? (
              <div className="om-guest-budget">
                <div className="om-guest-budget__row">
                  {budget.remaining <= 0 ? (
                    <span>
                      Daily budget used — all <b>{fmtOm(budget.limit)}</b> free tokens spent.
                    </span>
                  ) : (
                    <span>
                      Today&rsquo;s budget: <b>{fmtOm(budget.used)}</b> of{' '}
                      <b>{fmtOm(budget.limit)}</b> free tokens used ·{' '}
                      <b>{fmtOm(budget.remaining)}</b> left
                    </span>
                  )}
                  <span className="om-guest-budget__reset">
                    {budget.remaining <= 0 ? 'budget resets ' : 'resets '}
                    {new Date(budget.resetAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                  </span>
                </div>
                <div className="om-guest-budget__meter" role="img" aria-label={`${String(budget.used)} of ${String(budget.limit)} used`}>
                  <i style={{ width: `${String(Math.min(100, (budget.used / Math.max(1, budget.limit)) * 100))}%` }} />
                </div>
              </div>
            ) : (
              <p className="om-guest-budget__pending">
                Your daily budget appears here after your first message.
              </p>
            )}
            Guest sessions last up to 24 hours and aren&rsquo;t saved. A free account
            unlocks documents, durable memory, email &amp; SMS delivery, and bigger daily budgets.
          </div>
        ) : null}
      </div>
      <div className="om-body om-body--guest">
        {/* Mobile "Sessions" tab: saved conversations are an account
            feature — the padlock CTA mirrors the other locked panels. */}
        <section className="om-panel om-panel--sessions om-guest-sessions">
          <p className="om-guest-sessions__note">
            Guest conversations aren&rsquo;t saved — create a free account to keep and revisit
            your sessions.
          </p>
          <div className="om-guest-unlock">
            <Link to="/auth/signup" className="om-guest-unlock__btn">
              <span aria-hidden="true">🔒</span> Create Account to Save Conversations
            </Link>
          </div>
        </section>
        <ChatPanel
          agent={agent}
          items={items}
          status={status}
          onSend={sendMessage}
          onRetry={retryMessage}
          onFeedback={() => undefined}
          onNewSession={() => {
            setSessionRecords([]);
            setChatSessionId(newGuestChatSessionId());
          }}
          onChooseAgent={() => navigate('/')}
          guest
          {...(shouldShowAds(null) ? { topSlot: <AdSlot placement="chat" /> } : {})}
        />
        <InspectorPanel
          canvasRef={canvasRef}
          popRef={popRef}
          counts={counts}
          toolLog={toolLog}
          memorySections={
            sessionRecords.length > 0
              ? [
                  {
                    moduleId: 'session',
                    header: `SESSION — this conversation (${String(sessionRecords.length)})`,
                    records: sessionRecords,
                    trimmed: 0,
                  },
                ]
              : []
          }
          tools={toolsForAgent(agent.slug)}
          filesTree={[]}
          filesLoading={false}
          filesError={null}
          onOpenFile={() => undefined}
          onDeleteFile={() => undefined}
          guest
          {...(shouldShowAds(null) ? { topSlot: <AdSlot placement="panel" desktopOnly /> } : {})}
        />
      </div>
      <MobileNav active={view} onChange={setView} showJobs={false} />
    </div>
  );
}
